import { type MouseEvent as ReactMouseEvent, useCallback, useEffect, useRef, useState } from "react";

export const SPLIT_HANDLE_WIDTH = 8;
const FIRST_PANE_MIN_WIDTH = 420;
const SECOND_PANE_MIN_WIDTH = 360;

function firstPaneBounds(containerWidth: number) {
  const max = Math.max(0, containerWidth - SECOND_PANE_MIN_WIDTH - SPLIT_HANDLE_WIDTH);
  return { min: Math.min(FIRST_PANE_MIN_WIDTH, max), max };
}

function halfWidth(containerWidth: number) {
  return Math.round((containerWidth - SPLIT_HANDLE_WIDTH) * 0.5);
}

/**
 * A draggable two-pane horizontal split. The first pane's width is `null` until the container is
 * measured (render it at 50%), then clamped to the bounds whenever the container resizes.
 */
export function useSplitPane(disabled: boolean) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragStartRef = useRef<{ x: number; width: number } | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [firstPaneWidth, setFirstPaneWidth] = useState<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    setContainerWidth(Math.max(0, Math.floor(element.clientWidth)));
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? element.clientWidth;
      setContainerWidth(Math.max(0, Math.floor(width)));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (disabled) {
      setIsResizing(false);
      return;
    }
    if (containerWidth <= 0) return;

    const { min, max } = firstPaneBounds(containerWidth);
    setFirstPaneWidth(prev => (prev == null ? halfWidth(containerWidth) : Math.min(max, Math.max(min, prev))));
  }, [disabled, containerWidth]);

  useEffect(() => {
    if (!isResizing || disabled) return;

    const onMove = (event: MouseEvent) => {
      if (!dragStartRef.current) return;
      const { min, max } = firstPaneBounds(containerWidth);
      const next = dragStartRef.current.width + event.clientX - dragStartRef.current.x;
      setFirstPaneWidth(Math.min(max, Math.max(min, next)));
    };
    const onUp = () => {
      setIsResizing(false);
      dragStartRef.current = null;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isResizing, disabled, containerWidth]);

  const onResizeStart = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (disabled) return;
      event.preventDefault();
      dragStartRef.current = { x: event.clientX, width: firstPaneWidth ?? halfWidth(containerWidth) };
      setIsResizing(true);
    },
    [disabled, firstPaneWidth, containerWidth],
  );

  return { containerRef, firstPaneWidth, isResizing, onResizeStart };
}
